import React, { useState, useEffect } from 'react';
import {
    Container, Box, Typography, Button,
    Divider, CircularProgress,
    Chip, Collapse,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    LocationOn as LocationIcon,
    CheckCircle as CheckCircleIcon,
    Home as HomeIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    ShoppingCart as CartIcon,
    ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import '../../styles/ProtectedRoutes.css';
import API from '../../services/api';
import billService from '../../services/billService';
import cartService from '../../services/cartService';
import { useLocation, useNavigate } from 'react-router-dom';
import AddAddressDialog from './Addaddressdialog';
import EditAddressDialog from './EditAddressDialog';
import { useCart } from '../../context/CartContext';

// ── Address Card ──────────────────────────────────────────────────────────────
const AddressCard = ({ address, selected, onSelect, onEdit, onDelete, onSetDefault, onDeliver, delivering }) => {
    const isDefault = !!address.IsDefault;

    return (
        <Box
            onClick={() => onSelect(address.AddressId)}
            sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1.5,
                p: 'clamp(12px, 2.5vw, 20px)',
                borderRadius: 3,
                cursor: 'pointer',
                border: `1.5px solid ${selected ? 'rgba(188,150,94,0.55)' : 'rgba(188,150,94,0.15)'}`,
                background: selected
                    ? 'linear-gradient(135deg, rgba(188,150,94,0.1), rgba(188,150,94,0.04))'
                    : 'rgba(255,255,255,0.02)',
                boxShadow: selected ? '0 4px 18px rgba(188,150,94,0.15)' : 'none',
                transition: 'all 0.22s ease',
                '&:hover': {
                    border: '1.5px solid rgba(188,150,94,0.45)',
                    background: 'rgba(188,150,94,0.06)',
                    transform: { sm: 'translateY(-1px)' },
                },
            }}
        >
            {/* Radio dot */}
            <Box sx={{
                width: 18, height: 18, borderRadius: '50%', flexShrink: 0, mt: 0.4,
                border: `2px solid ${selected ? '#bc965e' : 'rgba(255,255,255,0.3)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'border-color 0.2s',
            }}>
                {selected && <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#bc965e' }} />}
            </Box>

            {/* Body */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Name + badge */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography sx={{ color: 'white', fontWeight: 600, fontSize: 'clamp(0.82rem, 1.6vw, 0.9rem)' }}>
                        {address.FullName}
                    </Typography>
                    {isDefault && (
                        <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: '12px !important', color: '#4caf50 !important' }} />}
                            label="Default" size="small"
                            sx={{
                                height: 18, fontSize: '0.6rem', fontWeight: 700,
                                backgroundColor: 'rgba(76,175,80,0.12)',
                                border: '1px solid rgba(76,175,80,0.35)',
                                color: '#4caf50',
                                '& .MuiChip-icon': { marginLeft: '4px' },
                            }}
                        />
                    )}
                </Box>

                {/* Address text */}
                <Typography sx={{
                    color: 'rgba(255,255,255,0.75)', lineHeight: 1.55,
                    fontSize: 'clamp(0.76rem, 1.5vw, 0.85rem)',
                    wordBreak: 'break-word',
                }}>
                    {address.Address}, {address.City}, {address.State} – {address.Pincode}, India
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mt: 0.25, fontSize: 'clamp(0.68rem, 1.3vw, 0.75rem)' }}>
                    📞 {address.PhoneNumber}
                </Typography>

                {/* Actions */}
                <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.25, flexWrap: 'wrap' }}
                    onClick={e => e.stopPropagation()}
                >
                    <Button size="small" onClick={() => onEdit(address)}
                        startIcon={<EditIcon sx={{ fontSize: '13px !important' }} />}
                        sx={{
                            textTransform: 'none', color: '#bc965e', borderRadius: 1.5,
                            p: '3px 8px', minWidth: 0,
                            fontSize: '0.72rem', fontWeight: 500,
                            '&:hover': { backgroundColor: 'rgba(188,150,94,0.1)', color: '#d4af37' },
                        }}>Edit</Button>

                    <Button size="small" onClick={() => onDelete(address.AddressId)}
                        startIcon={<DeleteIcon sx={{ fontSize: '13px !important' }} />}
                        sx={{
                            textTransform: 'none', color: 'rgba(255,100,100,0.8)', borderRadius: 1.5,
                            p: '3px 8px', minWidth: 0,
                            fontSize: '0.72rem', fontWeight: 500,
                            '&:hover': { backgroundColor: 'rgba(255,80,80,0.1)', color: '#ff6b6b' },
                        }}>Remove</Button>

                    {!isDefault && (
                        <Button size="small" onClick={() => onSetDefault(address.AddressId)}
                            sx={{
                                textTransform: 'none', color: 'rgba(255,255,255,0.5)', borderRadius: 1.5,
                                p: '3px 8px', minWidth: 0,
                                fontSize: '0.72rem', fontWeight: 500,
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.8)' },
                            }}>Set default</Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

const API_BASE_URL = process.env.REACT_APP_API_URL.replace('/api', '');
const FALLBACK_SM = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjUwIiBoZWlnaHQ9IjI1MCIgdmlld0JveD0iMCAwIDI1MCAyNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyNTAiIGhlaWdodD0iMjUwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0xMjUgNzVWMTc1TTc1IDEyNUgxNzUiIHN0cm9rZT0iIzk5OTk5OSIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiLz4KPHN2Zz4K';

// ── Collapsible Order Summary (used on mobile & desktop) ──────────────────────
const OrderSummaryContent = ({
    cartData, cartTotal, appliedCoupon, couponDiscount, finalTotal,
    selectedId, delivering, addresses, onDeliver,
    shippingCharge, shippingCourier, shippingLoading, shippingError,
}) => (
    <>
        {/* Items list */}
        <Box sx={{ mb: 2, maxHeight: { xs: 200, sm: 260, md: 300 }, overflowY: 'auto', pr: 0.5 }}>
            {cartData.items.map((item, idx) => {
                const photoUrl = item.Photo ? `${API_BASE_URL}${item.Photo}` : FALLBACK_SM;
                return (
                    <Box key={idx} sx={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                        mb: 1.25, pb: 1.25,
                        borderBottom: '1px solid rgba(188,150,94,0.12)',
                    }}>
                        <Box sx={{ display: 'flex', flex: 1, minWidth: 0, gap: 1.25 }}>
                            <Box
                                component="img"
                                src={photoUrl}
                                alt={item.ProductName}
                                onError={(e) => { if (e.target.src !== FALLBACK_SM) e.target.src = FALLBACK_SM; }}
                                sx={{
                                    width: 44, height: 44, flexShrink: 0,
                                    objectFit: 'cover', borderRadius: 0.5,
                                    backgroundColor: '#0f0f0f',
                                    border: '1px solid rgba(188,150,94,0.15)',
                                }}
                            />
                            <Box sx={{ minWidth: 0, pr: 1, textAlign: 'left' }}>
                                <Typography sx={{ color: 'white', fontSize: 'clamp(0.78rem, 1.6vw, 0.88rem)', wordBreak: 'break-word' }}>
                                    {item.ProductName}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block' }}>
                                    {item.Quantity} × ₹{parseFloat(item.Price || 0).toFixed(2)}
                                </Typography>
                            </Box>
                        </Box>
                        <Typography sx={{ color: '#bc965e', fontWeight: 600, flexShrink: 0, fontSize: 'clamp(0.78rem, 1.6vw, 0.875rem)' }}>
                            ₹{parseFloat(item.Subtotal || 0).toFixed(2)}
                        </Typography>
                    </Box>
                );
            })}
        </Box>

        <Divider className="glass-divider" sx={{ my: 1.5 }} />

        {/* Subtotal */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>Subtotal</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>₹{cartTotal.toFixed(2)}</Typography>
        </Box>

        {/* Coupon */}
        {appliedCoupon && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography sx={{ color: '#4caf50', fontSize: '0.85rem' }}>
                    Coupon ({appliedCoupon.CouponCode})
                </Typography>
                <Typography sx={{ color: '#4caf50', fontWeight: 600, fontSize: '0.85rem' }}>
                    −₹{couponDiscount.toFixed(2)}
                </Typography>
            </Box>
        )}

        {/* Shipping */}
        <Box sx={{ mb: 0.75 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
                    Shipping{shippingCourier ? ` (${shippingCourier})` : ''}
                </Typography>
                <Box sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                    {shippingLoading
                        ? <CircularProgress size={11} sx={{ color: 'rgba(255,255,255,0.4)', display: 'block' }} />
                        : shippingError
                            ? null
                            : shippingCharge === 0
                                ? <Typography sx={{ color: '#4caf50', fontWeight: 600, fontSize: '0.85rem' }}>Free</Typography>
                                : <Typography sx={{ color: '#bc965e', fontWeight: 600, fontSize: '0.85rem' }}>₹{parseFloat(shippingCharge).toFixed(2)}</Typography>
                    }
                </Box>
            </Box>

            {shippingError && (
                <Typography variant="caption" sx={{ color: '#ff6b6b', display: 'block', mt: 0.5, fontSize: '0.72rem', lineHeight: 1.4 }}>
                    ⚠️ {shippingError}
                </Typography>
            )}
        </Box>

        <Divider className="glass-divider" sx={{ my: 1.5 }} />

        {/* Total */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 'clamp(0.95rem, 2vw, 1.1rem)' }}>
                Total
            </Typography>
            <Typography sx={{ color: '#bc965e', fontWeight: 800, fontSize: 'clamp(1rem, 2.2vw, 1.25rem)' }}>
                ₹{finalTotal.toFixed(2)}
            </Typography>
        </Box>

        {/* Deliver button */}
        <Button
            variant="contained" fullWidth
            className="glass-button"
            sx={{
                textTransform: 'none', fontSize: 'clamp(0.82rem, 1.8vw, 0.95rem)', py: { xs: 1.1, sm: 1.3 },
                borderRadius: 2, fontWeight: 700,
                '&.Mui-disabled': {
                    background: 'rgba(188,150,94,0.15) !important',
                    color: 'rgba(255,255,255,0.35) !important',
                    cursor: 'not-allowed',
                },
            }}
            disabled={!selectedId || delivering || shippingLoading || !!shippingError}
            startIcon={delivering ? <CircularProgress size={15} color="inherit" /> : null}
            onClick={() => {
                const selected = addresses.find(a => a.AddressId === selectedId);
                if (selected) onDeliver(selected);
            }}
        >
            {shippingError ? 'Delivery unavailable' : delivering ? 'Placing order…' : 'Deliver to this address'}
        </Button>
    </>
);

// ── Main Component ────────────────────────────────────────────────────────────
const DeliveryAddresses = () => {
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [cartData, setCartData] = useState(null);
    const [cartLoading, setCartLoading] = useState(true);
    const [delivering, setDelivering] = useState(false);
    const [summaryExpanded, setSummaryExpanded] = useState(false);

    const [shippingCharge, setShippingCharge] = useState(0);
    const [shippingCourier, setShippingCourier] = useState('');
    const [shippingLoading, setShippingLoading] = useState(false);
    const [shippingError, setShippingError] = useState('');

    const [addOpen, setAddOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editingAddress, setEditingAddress] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();
    const appliedCoupon = location.state?.appliedCoupon || null;
    const { refreshCart } = useCart();

    useEffect(() => { fetchAddresses(); fetchCart(); }, []);

    const cartTotal = cartData?.items?.reduce((sum, item) => sum + parseFloat(item.Subtotal || 0), 0) ?? 0;
    const couponDiscount = appliedCoupon ? parseFloat(appliedCoupon.DiscountAmount || 0) : 0;
    const discountedTotal = appliedCoupon
        ? parseFloat(appliedCoupon.FinalAmount || Math.max(cartTotal - couponDiscount, 0))
        : cartTotal;
    const finalTotal = discountedTotal + shippingCharge;

    const fetchShippingCharge = async (pincode, orderValue) => {
        setShippingLoading(true);
        setShippingError('');
        try {
            const { data } = await API.post(
                '/bills/shipping-charge',
                { delivery_postcode: pincode, order_value: orderValue },
                { timeout: 65000 }
            );
            if (data.success) {
                setShippingCharge(data.shippingCharge);
                setShippingCourier(data.courier);
            } else {
                setShippingCharge(0);
                setShippingCourier('');
                setShippingError(data.message || 'Delivery not available');
            }
        } catch (err) {
            console.error('Shipping error:', err.message);
            setShippingCharge(0);
            setShippingCourier('');
            setShippingError(
                err.code === 'ECONNABORTED'
                    ? 'Shipping rate check timed out. Please try again.'
                    : 'Could not fetch shipping rate'
            );
        } finally {
            setShippingLoading(false);
        }
    };

    useEffect(() => {
        if (!selectedId || !addresses.length || !cartData) return;
        const addr = addresses.find(a => a.AddressId === selectedId);
        if (addr?.Pincode) fetchShippingCharge(addr.Pincode, discountedTotal);
    }, [selectedId, addresses, discountedTotal]);

    const fetchCart = async () => {
        try {
            setCartLoading(true);
            const response = await cartService.getCart();
            if (response.success) setCartData(response.data);
        } catch (error) {
            console.error('Failed to load cart:', error);
        } finally {
            setCartLoading(false);
        }
    };

    const fetchAddresses = async () => {
        try {
            setLoading(true);
            const { data } = await API.get('/customer-addresses');
            if (data.success) {
                setAddresses(data.data);
                const def = data.data.find(a => a.IsDefault);
                setSelectedId(def ? def.AddressId : data.data[0]?.AddressId ?? null);
            }
        } catch { toast.error('Failed to load addresses'); }
        finally { setLoading(false); }
    };

    const handleAdd = async (form) => {
        setSaving(true);
        try {
            const { data } = await API.post('/customer-addresses', form);
            if (data.success) {
                toast.success('Address added');
                setAddOpen(false);
                await fetchAddresses();
            }
        } catch { toast.error('Failed to add address'); }
        finally { setSaving(false); }
    };

    const handleEdit = async (form) => {
        setSaving(true);
        try {
            const { data } = await API.put(`/customer-addresses/${editingAddress.AddressId}`, form);
            if (data.success) {
                toast.success('Address updated');
                setEditOpen(false);
                setEditingAddress(null);
                await fetchAddresses();
            }
        } catch { toast.error('Failed to update address'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (addressId) => {
        if (!window.confirm('Remove this address?')) return;
        try {
            await API.delete(`/customer-addresses/${addressId}`);
            toast.success('Address removed');
            await fetchAddresses();
        } catch { toast.error('Failed to remove address'); }
    };

    const handleSetDefault = async (addressId) => {
        try {
            await API.put(`/customer-addresses/default/${addressId}`);
            toast.success('Default address updated');
            await fetchAddresses();
        } catch { toast.error('Failed to set default'); }
    };

    const handleDeliver = (address) => {
        if (!cartData?.items?.length) { toast.error('Your cart is empty'); return; }
        if (shippingError) { toast.error(shippingError); return; }

        navigate('/payment', {
            state: { address, appliedCoupon, cartData, cartTotal, shippingCharge, finalTotal },
        });
    };

    const openEdit = (address) => {
        setEditingAddress(address);
        setEditOpen(true);
    };

    if (loading) return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} className="glass-content-container">
                <CircularProgress size={40} className="glass-loading" />
                <Typography variant="body1" className="glass-text-secondary">Loading addresses…</Typography>
            </Box>
        </Container>
    );

    const summaryProps = {
        cartData, cartTotal, appliedCoupon, couponDiscount, finalTotal,
        selectedId, delivering, addresses, onDeliver: handleDeliver,
        shippingCharge, shippingCourier, shippingLoading, shippingError,
    };

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 1.5, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>

            {/* Header */}
            <Box sx={{ mb: { xs: 2, sm: 3 }, textAlign: 'left', position: 'relative' }} className="glass-content-container">
                <Button
                    onClick={() => navigate(-1)}
                    startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
                    sx={{
                        position: { sm: 'absolute' },
                        top: { sm: 0 },
                        left: { sm: 0 },
                        textTransform: 'none',
                        color: 'rgba(255,255,255,0.65)',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        px: 1,
                        mb: { xs: 1, sm: 0 },
                        minWidth: 0,
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)', color: '#bc965e' },
                    }}
                >
                    Back
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.75 }}>
                    <LocationIcon sx={{ color: '#bc965e', fontSize: 'clamp(22px, 4vw, 32px)' }} />
                    <Typography variant="h4" className="glass-title" sx={{ fontSize: 'clamp(1.15rem, 3.5vw, 2rem)' }}>
                        Delivery Addresses
                    </Typography>
                </Box>
                <Typography variant="body2" className="glass-text-secondary" sx={{ fontSize: 'clamp(0.76rem, 1.8vw, 0.875rem)', textAlign: 'center' }}>
                    Manage your saved delivery addresses
                </Typography>
            </Box>

            {/* ── Mobile/tablet: collapsible order summary strip ── */}
            {!cartLoading && cartData?.items?.length > 0 && (
                <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }} className="glass-content-container">
                    <Box
                        onClick={() => setSummaryExpanded(p => !p)}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <CartIcon sx={{ color: '#bc965e', fontSize: 18, flexShrink: 0 }} />
                            <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                                Order Summary
                            </Typography>
                            <Chip
                                label={`₹${finalTotal.toFixed(2)}`}
                                size="small"
                                sx={{
                                    height: 20, fontSize: '0.7rem', fontWeight: 700,
                                    backgroundColor: 'rgba(188,150,94,0.15)',
                                    border: '1px solid rgba(188,150,94,0.35)',
                                    color: appliedCoupon ? '#4caf50' : '#bc965e',
                                }}
                            />
                        </Box>
                        {summaryExpanded
                            ? <ExpandLessIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 20 }} />
                            : <ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 20 }} />
                        }
                    </Box>

                    <Collapse in={summaryExpanded} timeout={220}>
                        <Divider className="glass-divider" sx={{ my: 1.5 }} />
                        <OrderSummaryContent {...summaryProps} />
                    </Collapse>
                </Box>
            )}

            {/* ── Responsive two-column layout ── */}
            <Box sx={{
                display: 'flex',
                gap: { xs: 0, md: 2.5, lg: 3 },
                alignItems: 'flex-start',
                flexDirection: { xs: 'column', md: 'row' },
            }}>

                {/* Left — Addresses */}
                <Box sx={{ flex: '1 1 0', minWidth: 0, width: '100%' }} className="glass-content-container">
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        mb: { xs: 1.5, sm: 2 }, flexWrap: 'wrap', gap: 1,
                    }}>
                        <Typography variant="h6" className="glass-title" sx={{ fontSize: 'clamp(0.9rem, 2vw, 1.1rem)' }}>
                            Saved Addresses ({addresses.length})
                        </Typography>
                        <Button
                            variant="outlined" size="small"
                            startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                            onClick={() => setAddOpen(true)}
                            className="glass-button-outlined"
                            sx={{ textTransform: 'none', fontSize: '0.78rem', px: 1.5, borderRadius: 2 }}
                        >
                            Add New
                        </Button>
                    </Box>

                    {addresses.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 5 } }}>
                            <HomeIcon sx={{ fontSize: 'clamp(36px, 6vw, 52px)', color: 'rgba(188,150,94,0.3)', mb: 1 }} />
                            <Typography variant="body1" className="glass-text-secondary" sx={{ fontSize: 'clamp(0.85rem, 2vw, 1rem)' }}>
                                No saved addresses yet.
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 1.5 } }}>
                        {addresses.map((addr) => (
                            <AddressCard
                                key={addr.AddressId}
                                address={addr}
                                selected={selectedId === addr.AddressId}
                                onSelect={setSelectedId}
                                onEdit={openEdit}
                                onDelete={handleDelete}
                                onSetDefault={handleSetDefault}
                                onDeliver={handleDeliver}
                                delivering={delivering}
                            />
                        ))}
                    </Box>
                </Box>

                {/* Right — Order Summary (tablet/desktop) */}
                <Box sx={{
                    width: { md: 300, lg: 340 }, flexShrink: 0,
                    position: { md: 'sticky' }, top: { md: 20 },
                    display: { xs: 'none', md: 'block' },
                    mt: { md: 0 },
                }}>
                    {cartLoading ? (
                        <Box className="glass-content-container" sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={40} />
                        </Box>
                    ) : cartData?.items?.length > 0 ? (
                        <Box className="glass-content-container" sx={{ textAlign: 'left' }}>
                            <Typography variant="h6" className="glass-title" sx={{ mb: 2.5, fontSize: '1.05rem' }}>
                                Order Summary
                            </Typography>
                            <OrderSummaryContent {...summaryProps} />
                        </Box>
                    ) : (
                        <Box className="glass-content-container" sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" className="glass-text-secondary">Your cart is empty</Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Add Address Dialog */}
            <AddAddressDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onSave={handleAdd}
                saving={saving}
            />

            {/* Edit Address Dialog */}
            <EditAddressDialog
                open={editOpen}
                onClose={() => { setEditOpen(false); setEditingAddress(null); }}
                onSave={handleEdit}
                saving={saving}
                address={editingAddress}
            />

        </Container>
    );
};

export default DeliveryAddresses;